if (!customElements.get('collapsible-sidebar')) {
  class CollapsibleSidebar extends HTMLElement {
    constructor() {
      super();
      this.container = this.parentElement;
      this.toggles = document.querySelectorAll(`[aria-controls="${this.id}"]`);
      // TODO: Consider switching to JSON object with list of sidebars in future to better support separate preferences
      this.key = `${this.getAttribute('sidebar') ?? this.id}-collapsed`;
      this.loadCollapsed = localStorage.getItem(this.key) === 'true';
      this.isAnimating = false;
      this.animation = {
        ease: 'power3.inOut',
        duration: 0.8,
      };
      this.configureMQL();

      this.handleHideComplete = this.updateAttributes.bind(this, true);
      this.handleShowComplete = this.updateAttributes.bind(this, false);

      this.updateAttributes(this.loadCollapsed);
    }

    connectedCallback() {
      this.mm ? this.mm.add(this.mmQuery, () => {
        this.init();

        return this.destroy.bind(this);
      }) : this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      if (this.isCollapsed()) {
        gsap.set(this, { autoAlpha: 0 });
        this.updateAttributes(true);
        this.setCollapsed(true);
      }

      // Remove "collapsed" data attribute to allow for automatic width calculation in expanded state
      this.clearDocumentData();

      this.toggles.forEach(toggle => toggle.addEventListener('click', () => this.toggle(toggle)));
    }

    destroy() {
      this.setDocumentData();

      this.toggles.forEach(toggle => toggle.removeEventListener('click', () => this.toggle(toggle)));
    }

    // TODO: Finish this
    configureMQL() {
      const queryStrings = [];
      if (this.hasAttribute('viewport-min')) {
        queryStrings.push(breakpointQuery(this.getAttribute('viewport-min'), 'min'));
      }

      if (this.hasAttribute('viewport-max')) {
        queryStrings.push(breakpointQuery(this.getAttribute('viewport-max'), 'max'));
      }

      if (!queryStrings.length) return;

      this.mmQuery = queryStrings.join(' and ');

      this.mm = gsap.matchMedia();
    }

    parseViewportOption(option) {
      option = camelToKebab(option);

      if (!this.hasAttribute(option)) return null;
      const value = this.getAttribute(option);

      return parseAttribute(value);
    }

    isCollapsed() {
      return this.getAttribute('aria-hidden') === 'true';
    }

    updateAttributes(collapsed) {
      this.setAttribute('aria-hidden', collapsed);
      this.toggles.forEach(toggle => toggle.setAttribute('aria-expanded', !collapsed));
      localStorage.setItem(this.key, collapsed ? 'true' : 'false');
      this.isAnimating = false;
    }

    clearDocumentData() {
      delete document.documentElement.dataset.collapseFilters;
    }

    setDocumentData() {
      if (this.isCollapsed()) {
        document.documentElement.dataset.collapseFilters = '';
      }
    }

    setCollapsed(collapsed) {
      if (collapsed) {
        gsap.set(this.container, { gridTemplateColumns: '0 1fr' });
      } else {
        gsap.set(this.container, { clearProps: 'gridTemplateColumns' });
      }
    }

    hide() {
      const { duration, ease } = this.animation;

      this.setCollapsed(false);
      const state = Flip.getState([
        this.container.querySelectorAll('.grid__item'),
      ]);
      this.setCollapsed(true);

      this.isAnimating = true;
      Flip.from(state, {
        duration,
        ease,
        prune: true,
        simple: true,
        onComplete: this.handleHideComplete,
      }).to(this, {
        autoAlpha: 0,
        duration: duration * 0.75,
        ease,
      }, 0);
    }

    show() {
      const { duration, ease } = this.animation;

      this.setCollapsed(true);
      const state = Flip.getState([
        this.container.querySelectorAll('.grid__item'),
      ]);
      this.setCollapsed(false);

      this.isAnimating = true;
      Flip.from(state, {
        duration,
        ease,
        prune: true,
        simple: true,
        onComplete: this.handleShowComplete,
      }).to(this, {
        autoAlpha: 1,
        duration: duration * 0.75,
        ease,
      }, 0.25);
    }

    toggle(toggle) {
      if (this.isAnimating) return;

      if (toggle) {
        // For faster INP, immediately update the toggle icon
        const toggleIcon = toggle.querySelector(".facets-toggle__icon");
        toggleIcon.classList.toggle("facets-icon--active");
      }

      // Move the animation to the next frame to avoid blocking the main thread
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.isAnimating = true;
          if (this.isCollapsed()) {
            this.show();
          } else {
            this.hide();
          }
        });
      });
    }
  }

  customElements.define('collapsible-sidebar', CollapsibleSidebar);
}
